import { FormattedMessage } from "react-intl";
import {
  JOB_CATEGORY_NONE,
  JOB_EMPTY_PLACEHOLDER,
  JOB_LOCKED_PLACEHOLDER,
} from "constants/forms";
import { Table } from "core/table/Table";
import { Typography } from "core/typography/Typography";
import { useEffect, useState } from "react";
import { onAdd, onDelete, renderCommonBlock } from "utils/tableContentHelper";
import InputSelect from "core/inputs/input-select/InputSelect";
import {
  CONSULTANT_MISSION_STATUS,
  MODAL_CONTENT,
  POPUP_STATUS,
} from "constants/constants";
import { phoneSize } from "utils/screenSizes";
import AlertPopup from "components/alert-popup/AlertPopup";
import ModalFooter from "components/modal/ModalFooter";
import { formatDateToYYYYMMDD } from "utils/formatters";
import {
  ERROR_JOB_HISTORY,
  ERROR_MISSION_JOB,
  ERROR_ADD_JOB,
  ERROR_MISSION_STATUS,
  ERROR_JOB_HISTORY_MISSING,
} from "constants/popup-codes";
import { headerGenerator } from "utils/HeaderHelper";
import Proptypes from "prop-types";
import { getFormattedDate, getJobsInCategories } from "utils/functions";
import { getJobCategoryOptions, getTranslatedList } from "utils/helper";
import { useJobCategoryContext } from "api/settings/job-category/JobCategoryProvider";

const ManageJobHistory = ({
  jobs,
  getUpdatedValues,
  lang,
  userJobHistory,
  setElementsToDelete,
  onClose,
  onValidate,
  usersByIds,
  missionStatus,
}) => {
  const {
    getCategoryJobIds,
    jobCategoryByJobId,
    jobCategoryList,
    jobCategoryListGetter,
  } = useJobCategoryContext();
  const [data, setData] = useState(userJobHistory);
  const [initialData, setInitialData] = useState(userJobHistory);
  const [status, setStatus] = useState();
  const [code, setCode] = useState();
  const [reloadData, setReloadData] = useState(false);
  const [defaultDate, setDefaultDate] = useState(new Date());
  const [categoryByRowId, setCategoryByRowId] = useState({});

  useEffect(() => {
    if (
      initialData.length !== userJobHistory.length &&
      userJobHistory.length > 0
    ) {
      const newLastJob = userJobHistory[userJobHistory.length - 1];
      setData(userJobHistory);
      setInitialData(userJobHistory);
      const establishmentDate = new Date(newLastJob?.establishment_date);
      if (establishmentDate > new Date()) {
        setDefaultDate(establishmentDate);
      } else {
        setDefaultDate(new Date());
      }
    }
  }, [userJobHistory, data, reloadData]);

  useEffect(() => {
    jobCategoryListGetter();
  }, [jobCategoryListGetter]);

  const formattedJobs = getTranslatedList({ lang, list: jobs });
  const formattedCategories = getJobCategoryOptions({
    lang,
    list: jobCategoryList,
  });

  // `onAdd` numbers a new row with lodash `uniqueId()` ("1", "2", ...), which
  // collides with the database id of an existing line. Without the prefix the
  // two rows share a key: the added row would show the other one's category,
  // and changing it would blank the job of both.
  const rowKey = (row) => `${row?.newRow ? "new" : "row"}-${row?.id}`;

  // Category picked per row. LOCAL state, never written onto the row:
  // `hasRowChanged` compares rows with JSON.stringify, so adding a key the
  // server payload does not have would make every row look modified and resend
  // the whole table on save.
  //
  // A line no longer stores its category (redmine 12940) — the job implies it.
  // So the fallback is the category of the job already on the row, which is what
  // makes an existing line show its category and offer the right jobs without
  // the user touching anything.
  const categoryOf = (row) =>
    categoryByRowId[rowKey(row)] ??
    jobCategoryByJobId[row?.domain_id]?.id ??
    null;

  // Same strict rule as everywhere else: no category, no job on offer. The cell
  // is locked in that state anyway, and answering "every job" here was the one
  // place left where the option list and the pruning could disagree.
  const jobOptionsForCategory = (categoryId) =>
    getJobsInCategories(
      formattedJobs,
      categoryId ? getCategoryJobIds(categoryId) : []
    );

  // Writes one field on one row. Not `onChange` from tableContentHelper: that
  // one matches on `String(elm.id) === String(id)`, and a row added by `onAdd`
  // is numbered with lodash `uniqueId()` — "1", "2", ... — which collides with
  // the database id of an existing line and would write onto both.
  const setRowField = (row, key, value) => {
    const updatedData = data?.map((elm) =>
      rowKey(elm) === rowKey(row) ? { ...elm, [key]: value } : elm
    );
    setData(updatedData);
    getUpdatedValues(updatedData);
  };

  // Changing a row's category drops a job that category no longer offers. That
  // one IS a real change to the row, so it goes through setData and marks the
  // row as modified — unlike the category itself.
  const onCategoryChange = (row, val) => {
    // InputSelect hands back the raw value, and an empty string when cleared.
    const categoryId = val || null;
    if (categoryOf(row) === categoryId) return;
    setCategoryByRowId((prev) => ({ ...prev, [rowKey(row)]: categoryId }));

    // Removing the category leaves no job selectable on that row, so the job
    // goes with it.
    const keptJobs = jobOptionsForCategory(categoryId);
    if (
      row?.domain_id &&
      !keptJobs.some((job) => job.value === row.domain_id)
    ) {
      setRowField(row, "domain_id", null);
    }
  };

  const headerParams = [
    // The key is also the translation id, and `domain_category_id` is the one
    // that is translated. It never reaches a row: `handleAddJob` keeps this
    // column out of the header it hands to `onAdd`.
    { key: "domain_category_id", defaultMessage: "Catégorie", icon: "user" },
    { key: "domain_id", defaultMessage: "Métier", icon: "user" },
    {
      key: "establishment_date",
      defaultMessage: "Date de prise en compte",
      icon: "calendar",
    },
    { key: "updated_by", defaultMessage: "Modifié par", icon: "user" },
    { key: "updated_at", defaultMessage: "Le", icon: "calendar" },
  ];

  const header = headerGenerator(headerParams);

  const setError = (code) => {
    setCode(code);
    setStatus(POPUP_STATUS.ERROR);
    setReloadData(!reloadData);
  };

  const onClickValidate = () => {
    if (!data?.length) {
      setError(ERROR_JOB_HISTORY_MISSING);
    } else if (data.some((elm) => !Number.isInteger(elm?.domain_id))) {
      setError(ERROR_JOB_HISTORY);
    } else {
      onValidate();
    }
  };

  const formatContent = (content) =>
    content?.length > 0
      ? content?.map((elm, index) => {
          const dataTestIndex = content.length - 1 - index;
          const rowCategoryId = categoryOf(elm);
          // A line filed under the protected category matches no option: dash.
          // Keyed on the flag, not on a missing match, so a list still loading
          // does not flash dashes.
          const isProtectedCategory = jobCategoryList?.find(
            (category) => category.id === rowCategoryId
          )?.is_protected;
          const categoryLabel = formattedCategories?.find(
            (category) => category.value === rowCategoryId
          )?.label;
          const rowJobOptions = jobOptionsForCategory(rowCategoryId);
          if (elm?.establishment_date === null) {
            elm.establishment_date = formatDateToYYYYMMDD(defaultDate);
          }
          return {
            id: elm?.id,
            // Must stay between id and dav_function: Table renders cells in the
            // order of these keys, not by header id.
            domain_category: data[index]?.mission_id ? (
              <button
                style={{ padding: "unset", cursor: "unset", textAlign: "left" }}
                className="input-placeholder sourcesanspro-normal-gray-suit-18px violet"
                onClick={() => setError(ERROR_MISSION_JOB)}
                data-testid={`${MODAL_CONTENT.MANAGE_JOB_HISTORY}_modal-category-${dataTestIndex}`}
              >
                {categoryLabel ??
                  (isProtectedCategory && (
                    <FormattedMessage {...JOB_CATEGORY_NONE} />
                  ))}
              </button>
            ) : (
              // Plain dropdown rather than the search input the job cell
              // uses: a dozen categories fit on screen, and the list must be
              // readable without typing anything first.
              <InputSelect
                id={`job-history-category-${rowKey(elm)}`}
                name="domain_category_id"
                maxHeight={160}
                options={formattedCategories ?? []}
                value={rowCategoryId ?? ""}
                emptyLabel={isProtectedCategory ? JOB_CATEGORY_NONE : undefined}
                setValue={(_name, val) => onCategoryChange(elm, val)}
                dataTestId={`${MODAL_CONTENT.MANAGE_JOB_HISTORY}_modal-category-${dataTestIndex}`}
              />
            ),
            dav_function: data[index]?.mission_id ? (
              <button
                style={{ padding: "unset", cursor: "unset", textAlign: "left" }}
                className="input-placeholder sourcesanspro-normal-gray-suit-18px violet"
                onClick={() => setError(ERROR_MISSION_JOB)}
                data-testid={`${MODAL_CONTENT.MANAGE_JOB_HISTORY}_modal-function-${dataTestIndex}`}
              >
                {
                  formattedJobs?.filter((job) => job.value === elm.domain_id)[0]
                    ?.label
                }
              </button>
            ) : (
              // Same plain dropdown as the category cell above: the row is
              // narrowed to one category, so the jobs on offer fit on screen
              // and must be readable without typing anything first.
              <InputSelect
                id={`job-history-job-${rowKey(elm)}`}
                name="domain_id"
                maxHeight={160}
                options={rowJobOptions ?? []}
                value={elm?.domain_id ?? ""}
                // The category of the row comes first (redmine 12939). An
                // existing line resolves its own through `categoryOf`, so only
                // a freshly added line starts locked.
                readOnly={!rowCategoryId}
                emptyLabel={
                  rowCategoryId ? JOB_EMPTY_PLACEHOLDER : JOB_LOCKED_PLACEHOLDER
                }
                setValue={(_name, val) =>
                  setRowField(elm, "domain_id", val ?? null)
                }
                dataTestId={`${MODAL_CONTENT.MANAGE_JOB_HISTORY}_modal-function-${dataTestIndex}`}
              />
            ),
            establishment_date: (
              <div
                data-testid={`${MODAL_CONTENT.MANAGE_JOB_HISTORY}_modal-establishment_date-${dataTestIndex}`}
              >
                {getFormattedDate(elm?.establishment_date || defaultDate, lang)}
              </div>
            ),
            ...renderCommonBlock({
              data,
              getUpdatedValues,
              id: elm?.id,
              onDelete,
              setData,
              updated_at: elm.updated_at,
              updated_by: usersByIds.find((user) => user.id === elm.updated_by)
                ?.full_name,
              lang,
              setElementsToDelete,
              deleteError: !!data[index]?.mission_id,
              errorToDisplay: () => setError(ERROR_MISSION_JOB),
              deleteBtnTestId: `${MODAL_CONTENT.MANAGE_JOB_HISTORY}_modal-delete_btn-${dataTestIndex}`,
            }),
          };
        })
      : [];

  const handleAddJob = () => {
    if (
      missionStatus === CONSULTANT_MISSION_STATUS.FDM ||
      missionStatus === CONSULTANT_MISSION_STATUS.OK ||
      missionStatus === CONSULTANT_MISSION_STATUS.STATUS_1 ||
      missionStatus === CONSULTANT_MISSION_STATUS.STATUS_BACKUP
    ) {
      setError(ERROR_MISSION_STATUS);
    } else {
      data?.filter((item) => item.newRow).length === 1
        ? setError(ERROR_ADD_JOB)
        : // The category column is dropped from the header handed to `onAdd`:
          // it builds the new row from the header keys, and a line does not
          // carry its category any more (redmine 12940) — it would travel in
          // the creation payload as a field the API does not know.
          onAdd({
            header: header.filter(
              (elm) => elm?.label?.key !== "domain_category_id"
            ),
            data,
            setData,
          });
    }
  };

  return (
    <>
      <button
        className="button-style"
        onClick={() => {
          handleAddJob();
        }}
        data-testid={`${MODAL_CONTENT.MANAGE_JOB_HISTORY}_modal-add_job_btn`}
      >
        <Typography
          className="link-1 violet "
          type="h2"
          label={
            <>
              +{" "}
              <FormattedMessage
                id="add_job_history"
                defaultMessage="Ajouter un métier"
              />
            </>
          }
        />
      </button>
      <br />
      <AlertPopup
        code={code}
        setCode={setCode}
        setStatus={setStatus}
        status={status}
      />
      <Table
        id="job_history"
        header={header}
        content={formatContent(data)}
        tableOverflow={"auto"}
        tableWidth={phoneSize ? "max-content" : "100%"}
      />
      <br />
      <br />
      <ModalFooter
        onClose={onClose}
        onValidate={() => {
          onClickValidate();
          setReloadData(!reloadData);
        }}
        modalType={MODAL_CONTENT.MANAGE_JOB_HISTORY}
      />
    </>
  );
};

export default ManageJobHistory;

ManageJobHistory.propTypes = {
  jobs: Proptypes.array,
  getUpdatedValues: Proptypes.func,
  lang: Proptypes.string,
  userJobHistory: Proptypes.array,
  setElementsToDelete: Proptypes.func,
  onClose: Proptypes.func,
  onValidate: Proptypes.func,
  usersByIds: Proptypes.array,
  missionStatus: Proptypes.string,
};
